// @ts-nocheck
import { memo } from 'react';
import { motion } from 'framer-motion';
import {
  Mail, MessageSquare, Phone,
  MailOpen, MousePointerClick, CalendarCheck, PhoneIncoming,
  PhoneCall, RefreshCw, UserPlus, FileText, Tag, Zap,
  Clock, Calendar, ChevronDown, ClipboardList, ClipboardCheck, ListChecks,
} from 'lucide-react';
import { useRecallSequenceStore } from '../../../recalls/sequenceBuilderStore';
import type { SequenceNode, BlockProperties } from '../../../recalls/sequenceBuilderTypes';
import { useV1Scope } from '../SequenceV1Scope';

// ─── Icon maps ────────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.ReactNode> = {
  'trigger-new-contact':          <UserPlus size={16} />,
  'trigger-appointment-booked':   <CalendarCheck size={16} />,
  'trigger-form-submitted':       <FileText size={16} />,
  'trigger-tag-added':            <Tag size={16} />,
  'action-send-email':            <Mail size={22} />,
  'action-send-sms':              <MessageSquare size={22} />,
  'action-ai-voice-call':         <Phone size={22} />,
  'action-send-survey':           <ClipboardList size={22} />,
  'condition-email-opened':       <MailOpen size={16} />,
  'condition-link-clicked':       <MousePointerClick size={16} />,
  'condition-appointment-booked': <CalendarCheck size={16} />,
  'condition-call-received':      <PhoneIncoming size={16} />,
  'condition-ai-call-answered':   <PhoneCall size={16} />,
  'condition-followup-requested': <RefreshCw size={16} />,
  'condition-survey-submitted':   <ClipboardCheck size={16} />,
  'condition-survey-answered':    <ListChecks size={16} />,
};

// ─── Theme helpers ────────────────────────────────────────────────────────────

const ACTION_THEME = {
  email:   { bg: 'bg-elva-channel-email', ring: 'ring-elva-channel-email', hex: '#3A6EA5', light: 'bg-elva-channel-email-tint' },
  sms:     { bg: 'bg-elva-iris-plum', ring: 'ring-elva-iris-plum', hex: '#8E3567', light: 'bg-elva-tint-lavender' },
  call:    { bg: 'bg-elva-green', ring: 'ring-elva-green', hex: '#0F5C45', light: 'bg-elva-green-tint' },
  survey:  { bg: 'bg-elva-iris-coral', ring: 'ring-elva-iris-coral', hex: '#D8625F', light: 'bg-elva-tint-pink' },
  default: { bg: 'bg-elva-green-deep',  ring: 'ring-elva-green-deep',  hex: '#0A4530', light: 'bg-elva-green-tint' },
} as const;

type ActionThemeKey = keyof typeof ACTION_THEME;

function resolveConditionTheme(ancestorTypes: string[]): (typeof ACTION_THEME)[ActionThemeKey] {
  for (let i = ancestorTypes.length - 1; i >= 0; i--) {
    const t = ancestorTypes[i];
    if (t === 'action-send-email') return ACTION_THEME.email;
    if (t === 'action-send-sms') return ACTION_THEME.sms;
    if (t === 'action-ai-voice-call') return ACTION_THEME.call;
    if (t === 'action-send-survey') return ACTION_THEME.survey;
  }
  return ACTION_THEME.default;
}

// ─── Timer Badge (always expanded in Easy Mode) ───────────────────────────────

function getTimerLabel(props: BlockProperties): string {
  const mode = props.timerMode ?? 'wait';
  if (mode === 'schedule') {
    if (!props.scheduledDate) return 'Scheduled';
    const dateStr = new Date(props.scheduledDate).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
    const timeStr = props.scheduledTime ? ` at ${props.scheduledTime}` : '';
    return `${dateStr}${timeStr}`;
  }
  if (props.delayAmount) {
    return `Wait ${props.delayAmount} ${props.delayUnit ?? 'days'}`;
  }
  return 'Set delay';
}

interface EasyTimerBadgeProps {
  node: SequenceNode;
}

export function EasyTimerBadge({ node }: EasyTimerBadgeProps) {
  const timerIcon = (node.properties.timerMode ?? 'wait') === 'schedule'
    ? <Calendar size={14} />
    : <Clock size={14} />;

  return (
    <div
      data-id={`easy-timer-${node.id}`}
      className="flex flex-col items-center"
    >
      {/* Tight connector line above timer */}
      <div style={{ width: 2, height: 8, background: 'var(--elva-ink-3)' }} />
      <div className="flex items-center gap-1.5 h-7 px-3 rounded-full border border-[var(--cream-2)] bg-[var(--surface-mute)] text-[var(--ink-1)]">
        <span className="shrink-0 text-amber-400">{timerIcon}</span>
        <span className="text-[11px] font-medium whitespace-nowrap">
          {getTimerLabel(node.properties)}
        </span>
      </div>
      {/* Connector line below timer */}
      <div style={{ width: 2, height: 8, background: 'var(--elva-ink-3)' }} />
    </div>
  );
}

// ─── Simple downward arrow ────────────────────────────────────────────────────

interface EasyArrowProps {
  opacity?: number;
}

export function EasyArrow({ opacity = 1 }: EasyArrowProps) {
  return (
    <div
      data-id="easy-arrow"
      className="flex flex-col items-center"
      style={{ opacity }}
    >
      <div style={{ width: 2, height: 20, background: 'var(--elva-ink-3)' }} />
      <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
        <path d="M6 8L0 0H12L6 8Z" fill="var(--elva-ink-3)" />
      </svg>
    </div>
  );
}

// ─── Trigger Block ────────────────────────────────────────────────────────────

interface EasyTriggerBlockProps {
  node: SequenceNode;
  activeBranchIndex: number;
  onBranchSelect: (index: number) => void;
}

export function EasyTriggerBlock({ node, activeBranchIndex, onBranchSelect }: EasyTriggerBlockProps) {
  const selectBlock = useRecallSequenceStore((s) => s.selectBlock);
  const clearEditChat = useRecallSequenceStore((s) => s.clearEditChat);
  const openSidebar = useRecallSequenceStore((s) => s.openSidebar);
  const selectedBlockId = useRecallSequenceStore((s) => s.selectedBlockId);
  const isAiBuilding = useRecallSequenceStore((s) => s.isAiBuilding);
  const { entryNodeEditable } = useV1Scope();
  const isSelected = selectedBlockId === node.id;

  const hasBranches = node.triggerBranches && node.triggerBranches.length > 1;

  // V1 (§8.4.7 row 7): entry/trigger node is display-only.
  const interactive = !isAiBuilding && entryNodeEditable;

  const handleClick = (e: React.MouseEvent) => {
    if (!interactive) return;
    e.stopPropagation();
    selectBlock(node.id);
    clearEditChat();
    openSidebar('edit');
  };

  return (
    <div
      data-block="true"
      data-id={`easy-trigger-${node.id}`}
      className="w-full"
    >
      <motion.div
        whileHover={interactive ? { scale: 1.01 } : {}}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        onClick={handleClick}
        className={`
          w-full bg-elva-surface dark:bg-zinc-900
          border-2 border-red-400 dark:border-red-800
          rounded-xl shadow-md transition-all
          ${isAiBuilding ? 'cursor-default opacity-80' : interactive ? 'cursor-pointer' : 'cursor-default'}
          ${isSelected && interactive ? 'ring-2 ring-offset-2 ring-offset-elva-bg ring-elva-negative shadow-lg' : 'hover:shadow-md'}
        `}
      >
        {/* Main trigger row */}
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-500/20 flex items-center justify-center shrink-0 text-red-500">
            <Zap size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-elva-ink-3 uppercase tracking-wide mb-0.5">Trigger Action</p>
            <p className="text-sm font-bold text-elva-ink-1 truncate">{node.label}</p>
          </div>
        </div>

        {/* Branch icon grid — shown when trigger has multiple branches */}
        {hasBranches && node.triggerBranches && (
          <div className="border-t border-red-200 dark:border-red-800/50 px-4 py-2.5">
            <p className="text-[10px] text-elva-ink-3 mb-2 uppercase tracking-wide font-medium">Active Branch</p>
            <div
              data-id={`trigger-branch-grid-${node.id}`}
              className="flex flex-wrap gap-2"
              style={{ maxWidth: '100%' }}
            >
              {node.triggerBranches.map((branch, index) => {
                const isActive = activeBranchIndex === index;
                const branchIcon = ICON_MAP[branch.type] ?? <Zap size={14} />;
                return (
                  <motion.button
                    key={index}
                    data-id={`trigger-branch-tab-${index}`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onBranchSelect(index);
                    }}
                    className={`
                      flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all
                      ${isActive
                        ? 'bg-red-500 text-elva-surface shadow-elva-sm'
                        : 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-500/20'
                      }
                    `}
                  >
                    <span className="shrink-0">{branchIcon}</span>
                    <span className="truncate max-w-[80px]">{branch.label}</span>
                  </motion.button>
                );
              })}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

// ─── Action Block ─────────────────────────────────────────────────────────────

interface EasyActionBlockProps {
  node: SequenceNode;
}

export function EasyActionBlock({ node }: EasyActionBlockProps) {
  const selectedBlockId = useRecallSequenceStore((s) => s.selectedBlockId);
  const highlightedNodeId = useRecallSequenceStore((s) => s.highlightedNodeId);
  const selectBlock = useRecallSequenceStore((s) => s.selectBlock);
  const clearEditChat = useRecallSequenceStore((s) => s.clearEditChat);
  const openSidebar = useRecallSequenceStore((s) => s.openSidebar);
  const isAiBuilding = useRecallSequenceStore((s) => s.isAiBuilding);

  const isSelected = selectedBlockId === node.id;
  const isHighlighted = highlightedNodeId === node.id;

  const BgColorMap: Record<string, string> = {
    'action-send-email':    'bg-elva-channel-email-soft border-elva-channel-email/30',
    'action-send-sms':      'bg-elva-iris-plum-soft border-elva-iris-plum/30',
    'action-ai-voice-call': 'bg-elva-green-soft border-elva-green/30',
    'action-send-survey':   'bg-elva-iris-coral-soft border-elva-iris-coral/30',
  };
  const IconBgMap: Record<string, string> = {
    'action-send-email':    'text-elva-channel-email',
    'action-send-sms':      'text-elva-iris-plum',
    'action-ai-voice-call': 'text-elva-green',
    'action-send-survey':   'text-elva-iris-coral',
  };
  const highlightRingMap: Record<string, string> = {
    'action-send-email':    'ring-elva-channel-email',
    'action-send-sms':      'ring-elva-iris-plum',
    'action-ai-voice-call': 'ring-elva-green',
    'action-send-survey':   'ring-elva-iris-coral',
  };

  const iconCls = IconBgMap[node.type] ?? 'text-elva-channel-email';
  const highlightRing = highlightRingMap[node.type] ?? 'ring-elva-green';
  const subtitle = getActionSubtitle(node);

  const handleClick = (e: React.MouseEvent) => {
    if (isAiBuilding) return;
    e.stopPropagation();
    selectBlock(node.id);
    clearEditChat();
    openSidebar('edit');
  };

  return (
    <div
      data-block="true"
      data-id={`easy-action-${node.id}`}
      className="relative w-full"
    >
      {isHighlighted && !isAiBuilding && (
        <motion.div
          className={`absolute inset-0 rounded-xl ring-2 ${highlightRing} pointer-events-none`}
          animate={{ opacity: [0.3, 1, 0.3], scale: [1, 1.02, 1] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}
      <motion.div
        whileHover={isAiBuilding ? {} : { scale: 1.01 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        onClick={handleClick}
        className={`
          w-full flex items-center gap-3 px-4 py-3
          rounded-xl border transition-all
          ${isAiBuilding ? 'cursor-default opacity-90' : 'cursor-pointer'}
          ${BgColorMap[node.type] ?? 'bg-elva-channel-email-soft border-elva-channel-email/30'}
          ${isSelected && !isAiBuilding ? `ring-2 ring-offset-2 ring-offset-elva-bg ${highlightRing} shadow-lg` : 'hover:shadow-md'}
        `}
      >
        <div className={`w-11 h-11 bg-elva-surface dark:bg-black/20 rounded-xl flex items-center justify-center shrink-0 ${iconCls}`}>
          {ICON_MAP[node.type] ?? <Mail size={20} />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-elva-ink-1 truncate">{node.label}</p>
          {subtitle && <p className="text-xs text-elva-ink-3 truncate mt-0.5">{subtitle}</p>}
        </div>
        {node.properties.blockTag && (
          <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-elva-surface/60 dark:bg-black/30 text-elva-ink-1/70 border border-elva-border-soft/50">
            {node.properties.blockTag}
          </span>
        )}
      </motion.div>
    </div>
  );
}

function getActionSubtitle(node: SequenceNode): string | null {
  switch (node.type) {
    case 'action-send-email':
      return node.properties.emailTemplateId
        ? (node.properties.subject ?? 'Email selected')
        : 'Select email template';
    case 'action-send-sms':
      return node.properties.message
        ? node.properties.message.slice(0, 40) + '…'
        : 'Write your message';
    case 'action-ai-voice-call':
      return node.properties.callTemplateId ? 'Call template selected' : 'Select call template';
    case 'action-send-survey':
      return node.properties.surveyTemplateId ? 'Survey selected' : 'Select survey form';
    default:
      return null;
  }
}

// ─── Condition Block ──────────────────────────────────────────────────────────

interface EasyConditionBlockProps {
  node: SequenceNode;
  activeTab: 'yes' | 'no';
  onTabChange: (tab: 'yes' | 'no') => void;
}

export const EasyConditionBlock = memo(function EasyConditionBlock({
  node,
  activeTab,
  onTabChange,
}: EasyConditionBlockProps) {
  const selectedBlockId = useRecallSequenceStore((s) => s.selectedBlockId);
  const selectBlock = useRecallSequenceStore((s) => s.selectBlock);
  const clearEditChat = useRecallSequenceStore((s) => s.clearEditChat);
  const openSidebar = useRecallSequenceStore((s) => s.openSidebar);
  const getAncestorTypes = useRecallSequenceStore((s) => s.getAncestorTypes);
  const isAiBuilding = useRecallSequenceStore((s) => s.isAiBuilding);
  const { checkConditionMode } = useV1Scope();

  const isSelected = selectedBlockId === node.id;
  const ancestors = getAncestorTypes(node.id);
  const theme = resolveConditionTheme(ancestors);
  // V1 (§8.4.7 row 2): force Branch display when Check is disabled.
  const rawConditionMode = node.properties.conditionMode ?? 'branching';
  const conditionMode = checkConditionMode ? rawConditionMode : 'branching';
  const isBranchMode = conditionMode === 'branching';
  const andConditions = node.properties.andConditions ?? [];

  const handleClick = (e: React.MouseEvent) => {
    if (isAiBuilding) return;
    e.stopPropagation();
    selectBlock(node.id);
    clearEditChat();
    openSidebar('edit');
  };

  return (
    <div
      data-block="true"
      data-id={`easy-condition-${node.id}`}
      className="w-full"
    >
      <motion.div
        whileHover={isAiBuilding ? {} : { scale: 1.01 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        onClick={handleClick}
        className={`
          w-full rounded-xl text-elva-on-accent transition-all overflow-hidden p-2
          ${isAiBuilding ? 'cursor-default opacity-90' : 'cursor-pointer'}
          ${theme.bg}
          ${isSelected && !isAiBuilding ? `ring-2 ring-offset-2 ring-offset-elva-bg ${theme.ring} shadow-lg` : 'hover:shadow-md'}
        `}
      >
        {/* Condition title row */}
        <div className="px-1 py-3">
          <p className="text-[10px]  uppercase tracking-wide opacity-75 mb-0.5">Condition</p>
          <p className="text-sm font-semibold truncate">{node.label}</p>

          {/* AND conditions */}
          {andConditions.map((cond) => (
            <p key={cond.type} className="text-xs opacity-80 mt-1">
              & {cond.label}
            </p>
          ))}
        </div>

        {/* Tabs row — only in branch mode */}
        {isBranchMode && (
          <div
            data-id={`condition-tabs-${node.id}`}
            className="flex  bg-elva-surface/10 rounded-xl overflow-hidden p-1 gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            {(['yes', 'no'] as const).map((tab) => (
              <button
                key={tab}
                data-id={`condition-tab-${node.id}-${tab}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onTabChange(tab);
                }}
                className={`
                  flex-1 py-2 text-sm font-semibold uppercase tracking-wider transition-all rounded-xl
                  ${activeTab === tab
                    ? 'bg-[var(--cream-1)] text-[var(--ink-0)] shadow-[var(--sh-1)]'
                    : 'text-white/75 hover:text-white hover:bg-white/15'
                  }
                `}
              >
                {tab === 'yes' ? 'Yes' : 'No'}
              </button>
            ))}
          </div>
        )}

        {/* Check mode single tab */}
        {!isBranchMode && (
          <div className="flex border-t border-white/20">
            <div className="flex-1 py-2 text-xs font-bold uppercase tracking-wider text-center text-elva-surface/80">
              {node.properties.checkSelectedPath === 'no' ? 'No' : 'Yes'}
            </div>
          </div>
        )}
      </motion.div>

      {/* Branch arrows below condition */}
      {isBranchMode ? (
        <div
          data-id={`condition-arrows-${node.id}`}
          className="flex justify-around  mt-1 relative h-6 w-full"
        >
          <div className="absolute -top-1 transition-all duration-300"
          style={{ left: activeTab === 'yes' ? 'calc(50% - 6px)' : '25%' }}
          >
          <EasyArrow opacity={activeTab === 'yes' ? 1 : 0.2} />
          </div>
          <div className="absolute -top-1 transition-all duration-300"
          style={{ left: activeTab === 'no' ? 'calc(50% - 6px)' : '75%' }}
          >
            <EasyArrow opacity={activeTab === 'no' ? 1 : 0.2} />
          </div>
        </div>
      ) : (
        <div className="flex justify-center mt-1">
          <EasyArrow />
        </div>
      )}
    </div>
  );
});

// ─── Add Block Button ─────────────────────────────────────────────────────────

interface EasyAddBlockButtonProps {
  afterNodeId: string;
  branch: 'next' | 'yes' | 'no' | `trigger-branch-${number}`;
}

export function EasyAddBlockButton({ afterNodeId, branch }: EasyAddBlockButtonProps) {
  const openSidebar = useRecallSequenceStore((s) => s.openSidebar);
  const setCtx = useRecallSequenceStore((s) => s.setAddBlockContext);
  const isAiBuilding = useRecallSequenceStore((s) => s.isAiBuilding);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCtx({ afterNodeId, branch });
    openSidebar('block-list');
  };

  // Hide the add button while AI is building the sequence
  if (isAiBuilding) return null;

  return (
    <div
      data-id={`easy-add-btn-${afterNodeId}-${branch}`}
      className="flex flex-col items-center"
    >
      <div style={{ width: 2, height: 16, background: 'var(--elva-ink-3)' }} />
      <motion.button
        data-add-btn="true"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleClick}
        className="flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-dashed border-elva-border-soft/60 hover:border-elva-iris-plum/60 hover:bg-elva-iris-plum-soft text-elva-ink-3 hover:text-elva-iris-plum transition-all text-xs font-medium"
      >
        <ChevronDown size={14} />
        Add Block
      </motion.button>
    </div>
  );
}

